import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DashTiles, ClaimTable } from "@/components/reimb/ReimbParts";
import { ReimbReports } from "@/components/reimb/ReimbReports";
import { ReimbSettings } from "@/components/reimb/ReimbSettings";

const STATUSES = ["", "draft", "submitted", "pending_approval", "returned", "rejected", "approved", "payment_pending", "on_hold", "partially_paid", "paid", "cancelled"];

export default function Reimbursements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("mine");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);

  useEffect(() => { api.get("/reimbursements/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const scope = tab === "mine" ? "me" : tab === "team" || tab === "approvals" ? "team" : "all";
  const load = useCallback(async () => {
    if (["reports", "settings"].includes(tab)) return;
    const view = tab === "accounts" ? "all" : tab;
    const [l, d] = await Promise.all([
      api.get(`/reimbursements?view=${view}${status ? `&status=${status}` : ""}`),
      api.get(`/reimbursements/dashboard?scope=${scope}`),
    ]);
    setRows(l.data); setDash(d.data);
  }, [tab, status, scope]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const isAccounts = meta?.is_accounts || meta?.is_final || user?.role === "hr";
  const tabs = [["mine", "My Claims"], ["approvals", "To Approve"], ["team", "My Team"], isAccounts && ["accounts", "Accounts Desk"], ["reports", "Reports"], meta?.can_settings && ["settings", "Settings"]].filter(Boolean);

  return (
    <div className="space-y-4" data-testid="reimbursements-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Finance · Employee claims</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight uppercase">Reimbursements</h1>
        </div>
        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => navigate("/reimbursements/new")} data-testid="new-claim-button"><Plus className="w-4 h-4 mr-1" /> New Claim</Button>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto" data-testid="reimb-tabs">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setStatus(""); }} data-testid={`reimb-tab-${k}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-b-2 whitespace-nowrap transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{label}</button>))}
      </div>

      {tab === "reports" && <ReimbReports />}
      {tab === "settings" && <ReimbSettings meta={meta} />}
      {!["reports", "settings"].includes(tab) && (
        <>
          {dash && <DashTiles d={dash} />}
          <div className="flex items-center gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-xs" data-testid="reimb-status-filter">
              {STATUSES.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All statuses"}</option>)}
            </select>
            <span className="text-xs text-muted-foreground">{rows.length} claim(s)</span>
          </div>
          <ClaimTable rows={rows} showEmployee={tab !== "mine"} />
        </>
      )}
    </div>
  );
}
